import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Check } from "lucide-react";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";
import { STATS } from "@/data/site";

const Counter = ({ value, suffix, raw }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    if (raw) {
      setDisplay(value);
      return;
    }
    let start = 0;
    const duration = 1200;
    const startTime = performance.now();
    let frame;
    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(eased * value));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, value, raw]);

  return (
    <span ref={ref}>
      {display}
      {suffix}
    </span>
  );
};

export const About = () => {
  const highlights = [
    "100% Geld-zurück-Garantie",
    "Transparente Fixpreise",
    "Laufende Updates & Wartung",
    "Neue Apps, individuell entwickelt",
  ];

  return (
    <section
      id="ueber-uns"
      data-testid="about-section"
      className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28"
    >
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="lg:col-span-6"
        >
          <motion.div variants={fadeUp} className="lx-kicker mb-3">
            Über uns
          </motion.div>
          <motion.h2
            variants={fadeUp}
            className="font-display text-2xl font-semibold tracking-tight text-[color:var(--lx-text)] sm:text-3xl lg:text-4xl"
          >
            <span className="font-editorial font-normal italic" style={{ color: "var(--lx-bronze-2)" }}>
              LiberoX Labs
            </span>{" "}
            ist die Produkt-Schmiede der Liberox GmbH.
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="mt-5 text-base leading-relaxed text-[color:var(--lx-text-2)]"
          >
            Wir sind ein junges, fokussiertes Team aus Designer:innen und
            Ingenieur:innen mit einer klaren Mission: digitale Produkte zu bauen,
            die man gerne benutzt. Die Liberox GmbH gibt es seit 2022, und mit
            LiberoX Labs entwickeln wir Apps und Web-Plattformen von der ersten Idee bis zum
            erfolgreichen Launch neu.
          </motion.p>
          <motion.p
            variants={fadeUp}
            className="mt-4 text-base leading-relaxed text-[color:var(--lx-text-2)]"
          >
            Qualität ist für uns kein Feature, sondern der Standard. Als junges
            Team setzen wir auf maximale Transparenz: klare Fixpreise, ehrliche
            Kommunikation und eine App, die sich premium anfühlt.
          </motion.p>

          <motion.ul variants={fadeUp} className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {highlights.map((h) => (
              <li key={h} className="flex items-start gap-2.5 text-sm text-[color:var(--lx-text-2)]">
                <span
                  className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full"
                  style={{ background: "rgba(168,232,92,0.15)", color: "var(--lx-green)" }}
                >
                  <Check size={12} />
                </span>
                {h}
              </li>
            ))}
          </motion.ul>
        </motion.div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="lg:col-span-6"
        >
          <div className="grid grid-cols-2 gap-4">
            {STATS.map((s) => (
              <motion.div
                key={s.label}
                variants={fadeUp}
                data-testid="about-stat"
                className="lx-card flex flex-col justify-between p-6"
              >
                <div className="font-display text-4xl font-semibold tracking-tight text-[color:var(--lx-text)]">
                  <Counter value={s.value} suffix={s.suffix} raw={s.raw} />
                </div>
                <div className="mt-3 font-mono-lx text-xs uppercase tracking-wider text-[color:var(--lx-muted)]">
                  {s.label}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
